<template>
  <VDropdown
    ref="vDropdown"
    :theme="theme"
    :placement="placement"
    :skidding="skidding"
    :distance="distance"
    :popperClass="`tooltip-dropdown__popper ${popperClass}`"
    :triggers="triggers"
    :popperHideTriggers="popperHideTriggers"
    :popperShowTriggers="popperShowTriggers"
    :shown="shown"
    :class="[`tooltip-dropdown ${dropdownClass}`]"
    :autoHide="autoHide"
    :disabled="disabled"
    @applyShow="onShowDropdown"
    @applyHide="onHideDropdown"
  >
    <button
      class="tooltip-dropdown__label"
      :tabindex="tabIndex"
      type="button"
      :class="[`icon-button__${size}`, { 'is-active': openDropdown }]"
    >
      <slot name="tooltip-label">
        <icon
          data="@icon/more-vertical.svg"
          :width="mainIconSize"
          :height="mainIconSize"
          :fill="false"
        />
      </slot>
    </button>

    <template #popper="{ hide }">
      <slot name="before" />

      <slot
        name="tooltip-content"
        :hide="hide"
      >
        <div
          class="tooltip-dropdown__content"
          :class="subDropdownClass"
        >
          <VDropdown
            v-for="(item, index) in menu"
            :key="index"
            theme="info-dropdown"
            :placement="placementSub"
            :skidding="skiddingSub"
            :distance="distanceSub"
            :popperClass="popperClassSub"
            :shown="shownSub"
            :triggers="subTriggers"
            :disabled="disabledSub || item.hideOptions"
            @applyShow="$emit('onShowSubDropdown')"
            @applyHide="$emit('onHideSubDropdown')"
          >
            <BaseSubHeading
              size="large"
              weight="400"
              :className="subMenuClass"
            >
              <button
                :id="item.id"
                tabindex="-1"
                v-close-popper.all
                class="body-text body-text-medium-400 tooltip-submenu__button"
                @click="dropdownSubMenu(item, hide)"
              >
                <slot
                  name="tooltip-item"
                  :item="item"
                >
                  {{ item.label }}
                </slot>
              </button>
            </BaseSubHeading>
            <template
              v-if="$wait.is(submenuWaitTitle)"
              #popper
            >
              Loading...
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #popper="{ hide }">
              <template v-if="item.options?.length === 0">
                <slot
                  name="tooltip-submenu-empty"
                  :item="item"
                >
                  {{ submenuEmptyTitle }}
                </slot>
              </template>
              <template v-else>
                <slot
                  name="tooltip-subContent"
                  :hide="hide"
                >
                  <div class="tooltip-dropdown__content">
                    <BaseSubHeading
                      v-for="(option, key) in item.options"
                      :key="key"
                      size="medium"
                      weight="400"
                      :class="[optionMenuClass, `option-menu_${item.id}`]"
                    >
                      <button
                        :id="option.id"
                        tabindex="-1"
                        class="option-menu_button"
                        @click="dropdownOptionMenu(option, hide, item)"
                      >
                        <slot
                          name="tooltip-submenu-item"
                          :option="option"
                          :item="item"
                          :hide="hide"
                        >
                          {{ option.label }}
                        </slot>
                      </button>
                    </BaseSubHeading>
                  </div>
                </slot>
              </template>
            </template>
          </VDropdown>
        </div>
      </slot>
      <slot name="after" />
    </template>
  </VDropdown>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import { directive as ClickOutside } from "click-outside-vue3"

type IconSize = "sm" | "md" | "none"

@Options({
  name: "DropdownTooltip",
  directives: {
    ClickOutside
  }
})
export default class extends Vue {
  @Prop({ default: "info-dropdown" }) readonly theme: string
  @Prop({ default: "sm" }) iconSize: IconSize
  @Prop({ default: 0 }) readonly skidding: number
  @Prop({ default: ["click"] }) readonly triggers: string[]
  @Prop({ default: ["click"] }) readonly hideTriggers: string[]
  @Prop({ default: 0 }) readonly distance: number
  @Prop({ default: "bottom" }) readonly placement: string | undefined
  @Prop({ default: "" }) readonly popperClass: string
  @Prop({ default: "" }) readonly dropdownClass: string
  @Prop({ default: false }) readonly shown: boolean
  @Prop({ default: false }) readonly shownSub: boolean

  @Prop({ default: [] }) readonly popperHideTriggers: string[]
  @Prop({ default: [] }) readonly popperShowTriggers: string[]
  @Prop({ default: true }) readonly autoHide: boolean

  @Prop({ default: ["click"] }) readonly subTriggers: string[]
  @Prop({ default: "sm" }) iconSizeSub: IconSize
  @Prop({ default: 0 }) readonly skiddingSub: number
  @Prop({ default: 0 }) readonly distanceSub: number
  @Prop({ default: "bottom" }) readonly placementSub: string
  @Prop({ default: "bottom" }) readonly popperClassSub: string
  @Prop({ default: false }) readonly disabledSub: boolean
  @Prop({ default: false }) readonly disabled: boolean

  @Prop({}) readonly menu: any[]
  @Prop({ default: "" }) readonly subMenuClass: string
  @Prop({ default: "" }) readonly submenuWaitTitle: string
  @Prop({ default: "No Options" }) readonly submenuEmptyTitle: string
  @Prop({ default: "" }) readonly subDropdownClass: string
  @Prop({ default: "" }) readonly optionMenuClass: string
  @Prop({ default: "var(--icon-24)" }) readonly mainIconSize: string
  @Prop({ default: 0 }) readonly tabIndex: number

  openDropdown = false

  get size() {
    switch (this.iconSize) {
      case "sm":
        return "sm"
      case "md":
        return "md"
      case "none":
        return "none"
      default:
        return "sm"
    }
  }

  @Emit()
  onShowDropdown() {
    this.openDropdown = true
  }

  @Emit()
  onHideDropdown() {
    this.openDropdown = false
  }

  @Emit()
  dropdownSubMenu(value, hide) {
    return {
      id: value.id,
      hide: hide
    }
  }

  @Emit()
  dropdownOptionMenu(value, hide, item) {
    return { value, hide, item }
  }
}
</script>
